import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toPng } from "html-to-image";
import {
  ArrowRight, Loader2, RefreshCcw, Sparkles, ShieldCheck, Download, Share2,
  CheckCircle2, AlertCircle, MapPin, GraduationCap,
} from "lucide-react";
import ToolShell from "@/components/ToolShell";
import { US_STATES, gradeColors } from "@/lib/affordability";
import { parseIncome, parseGpa, parseStateCode, resolveDreamSchools, hashInput } from "@/lib/find-form";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_PREFIX = "aidwaive:find:v2:";
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Find Schools, the unified entry point that runs Tool 1
 * (Affordability for the user's dream schools) and Tool 2 (Hidden Gem
 * Finder) IN PARALLEL on a single submit. Free-text form, verified-
 * data-in-prompt AI, 7-day localStorage cache by full-input hash.
 */
export default function FindSchoolsPage() {
  const [allSchools, setAllSchools] = useState([]);

  const [incomeRaw, setIncomeRaw] = useState("");
  const [stateRaw, setStateRaw] = useState("");
  const [gpaRaw, setGpaRaw] = useState("");
  const [major, setMajor] = useState("");
  const [dreamText, setDreamText] = useState("");
  const [factorsText, setFactorsText] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const [error, setError] = useState(null);

  // Load the seed once for client-side dream-school resolution.
  useEffect(() => {
    const cached = readCache(CACHE_PREFIX + "all-v2");
    if (cached) { setAllSchools(cached); return; }
    fetch(`${API}/netprice/all`).then(r => r.json()).then(j => {
      const rows = j.schools || [];
      writeCache(CACHE_PREFIX + "all-v2", rows);
      setAllSchools(rows);
    }).catch(() => {});
  }, []);

  const income = parseIncome(incomeRaw);
  const state = parseStateCode(stateRaw);
  const gpa = parseGpa(gpaRaw);
  const ready = income && (state || stateRaw === "") && gpa && (dreamText.trim().length > 4);

  const resolvedDreams = useMemo(
    () => resolveDreamSchools(dreamText, allSchools, 5),
    [dreamText, allSchools]
  );

  async function submit() {
    if (!ready) return;
    setError(null);
    setFromCache(false);
    const payload = {
      income, state, gpa, major: major || null,
      dream_text: dreamText, factors_text: factorsText,
      dream_school_ids: resolvedDreams.map(s => s.id),
    };
    const key = CACHE_PREFIX + hashInput(payload);
    const cached = readCache(key);
    if (cached) {
      setResults(cached);
      setFromCache(true);
      return;
    }
    setSubmitting(true);
    try {
      const [aff, gems] = await Promise.all([
        fetch(`${API}/find/affordability`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }).then(r => r.json()),
        fetch(`${API}/find/gems`,         { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }).then(r => r.json()),
      ]);
      const data = { aff, gems, payload, resolvedDreams: resolvedDreams.map(s => ({ id: s.id, name: s.name, state: s.state })) };
      writeCache(key, data);
      setResults(data);
    } catch (e) {
      setError("Something went wrong. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  }

  function reset() {
    setResults(null); setFromCache(false); setError(null);
  }

  return (
    <ToolShell
      tool="Find Schools · Tools 10 + 11"
      title="Find schools that fit your real life."
      subtitle="One form, two tools running together. Get a personalized affordability grade for your dream schools and discover 5 hidden gems most people miss."
      wide>
      {!results ? (
        <FormCard
          incomeRaw={incomeRaw} setIncomeRaw={setIncomeRaw}
          stateRaw={stateRaw} setStateRaw={setStateRaw}
          gpaRaw={gpaRaw} setGpaRaw={setGpaRaw}
          major={major} setMajor={setMajor}
          dreamText={dreamText} setDreamText={setDreamText}
          factorsText={factorsText} setFactorsText={setFactorsText}
          income={income} state={state} gpa={gpa}
          resolvedDreams={resolvedDreams}
          ready={ready} submitting={submitting}
          error={error} onSubmit={submit}
        />
      ) : (
        <ResultsView results={results} fromCache={fromCache} onReset={reset} />
      )}
    </ToolShell>
  );
}

/* ─── Form ───────────────────────────────────────────────────────── */

function FormCard(props) {
  const {
    incomeRaw, setIncomeRaw, stateRaw, setStateRaw, gpaRaw, setGpaRaw,
    major, setMajor, dreamText, setDreamText, factorsText, setFactorsText,
    income, state, gpa, resolvedDreams, ready, submitting, error, onSubmit,
  } = props;

  return (
    <div className="space-y-6 max-w-2xl">
      <Field label="What is your household income?" hint={income ? `Read as $${income.toLocaleString()}/yr` : "Type a number, with or without dollar signs and commas"}>
        <input type="text" inputMode="numeric" value={incomeRaw} onChange={e => setIncomeRaw(e.target.value)}
          placeholder="e.g. $67,000" className="aw-input" data-testid="find-income" />
      </Field>

      <Field label="What state are you from?" hint={state ? `Matched to ${US_STATES.find(s => s.code === state).name}` : "Type your state name or 2-letter code"}>
        <input type="text" list="aw-states" value={stateRaw} onChange={e => setStateRaw(e.target.value)}
          placeholder="e.g. Tennessee" className="aw-input" data-testid="find-state" />
        <datalist id="aw-states">
          {US_STATES.map(s => <option key={s.code} value={s.name} />)}
        </datalist>
      </Field>

      <Field label="What is your GPA?" hint={gpa != null ? `Read as ${gpa.toFixed(2)} on a 4.0 scale` : "On a 4.0 scale, weighted is fine"}>
        <input type="text" inputMode="decimal" value={gpaRaw} onChange={e => setGpaRaw(e.target.value)}
          placeholder="e.g. 3.7" className="aw-input" data-testid="find-gpa" />
      </Field>

      <Field label="What do you want to study?" hint="Be specific or say 'undecided'">
        <input type="text" value={major} onChange={e => setMajor(e.target.value)}
          placeholder="e.g. computer science or undecided" className="aw-input" data-testid="find-major" />
      </Field>

      <Field
        label="What are your dream schools and why?"
        hint={resolvedDreams.length
          ? `Detected: ${resolvedDreams.map(s => s.name).join(", ")}`
          : "Type freely. We will pick out the school names automatically."}>
        <textarea rows={3} value={dreamText} onChange={e => setDreamText(e.target.value)}
          placeholder="e.g. Vanderbilt because I want strong pre-med in the South. Also looking at Duke and UNC."
          className="aw-input min-h-24" data-testid="find-dream" />
      </Field>

      <Field label="Any other factors that matter?" hint="Optional. The more you say, the better the matches">
        <textarea rows={3} value={factorsText} onChange={e => setFactorsText(e.target.value)}
          placeholder="e.g. want to stay within 6 hours of home, need strong merit aid, love campus life"
          className="aw-input min-h-24" data-testid="find-factors" />
      </Field>

      {error && (
        <div className="flex items-center gap-2 text-sm" style={{ color: "#b91c1c" }} data-testid="find-error">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      <button
        type="button"
        onClick={onSubmit}
        disabled={!ready || submitting}
        className="inline-flex items-center gap-2 px-5 h-12 rounded-md text-sm font-medium disabled:opacity-50 hover:opacity-90"
        style={{ background: "#1d4ed8", color: "#ffffff" }}
        data-testid="find-submit">
        {submitting
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Running both tools</>
          : <>Grade my dream schools and find hidden gems <ArrowRight className="w-4 h-4" /></>}
      </button>

      <p className="text-xs text-gray-500">
        Two AI calls run in parallel. Your inputs are hashed and cached locally for 7 days, so identical
        searches never call the AI twice.
      </p>

      <style>{`
        .aw-input { width: 100%; height: 44px; padding: 0 12px; border: 1px solid #bfdbfe; border-radius: 8px; background: #fff; color: #111928; font-size: 14px; outline: none; transition: border-color 150ms ease, box-shadow 150ms ease; }
        .aw-input:focus { border-color: #1d4ed8; box-shadow: 0 0 0 3px rgba(29,78,216,0.12); }
        textarea.aw-input { padding: 10px 12px; height: auto; line-height: 1.5; }
      `}</style>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-900 mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-500 mt-1.5">{hint}</p>}
    </div>
  );
}

/* ─── Results ─────────────────────────────────────────────────────── */

function ResultsView({ results, fromCache, onReset }) {
  const cardRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  async function downloadCard() {
    if (!cardRef.current) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(cardRef.current, { cacheBust: true, pixelRatio: 2, backgroundColor: "#ffffff" });
      const a = document.createElement("a");
      a.download = "aidwaive-find-schools.png";
      a.href = dataUrl;
      a.click();
    } finally { setExporting(false); }
  }
  async function shareCard() {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, backgroundColor: "#ffffff" });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "aidwaive-find-schools.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My AidWaive Find Schools results" });
      } else if (navigator.share) {
        await navigator.share({ title: "AidWaive", url: window.location.href });
      } else {
        downloadCard();
      }
    } catch {}
  }

  return (
    <div className="space-y-7">
      {fromCache && (
        <div className="rounded-md p-3 text-xs flex items-center gap-2"
          style={{ background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e" }}
          data-testid="find-cache-banner">
          <ShieldCheck className="w-3.5 h-3.5" /> Showing your last AidWaive search. No new AI calls were made.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={onReset}
          className="inline-flex items-center gap-2 px-4 h-10 rounded-md text-sm font-medium border border-gray-200 hover:bg-gray-50"
          data-testid="find-reset">
          <RefreshCcw className="w-3.5 h-3.5" /> New search
        </button>
        <button onClick={shareCard}
          className="inline-flex items-center gap-2 px-4 h-10 rounded-md text-sm font-medium hover:opacity-90"
          style={{ background: "#1d4ed8", color: "#ffffff" }}
          data-testid="find-share">
          <Share2 className="w-3.5 h-3.5" /> Share
        </button>
        <button onClick={downloadCard} disabled={exporting}
          className="inline-flex items-center gap-2 px-4 h-10 rounded-md text-sm font-medium border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
          data-testid="find-download">
          {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          {exporting ? "Building" : "Download PNG"}
        </button>
      </div>

      <div ref={cardRef} className="rounded-2xl bg-white p-6 sm:p-8 space-y-8" style={{ border: "1px solid #bfdbfe" }}
        data-testid="find-card">
        <Header payload={results.payload} />
        <DreamSchoolsBlock cards={results.aff?.cards || []} />
        <HiddenGemsBlock data={results.gems || {}} />
        <Footer />
      </div>
    </div>
  );
}

function Header({ payload }) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-gray-400 mb-1">AidWaive Find Schools</div>
        <div className="font-heading text-2xl sm:text-3xl text-gray-900 leading-tight">
          ${payload.income.toLocaleString()} family
          {payload.state && <span className="text-gray-500"> · {payload.state}</span>}
          {payload.gpa && <span className="text-gray-500"> · {payload.gpa.toFixed(1)} GPA</span>}
        </div>
      </div>
      <div className="font-heading italic text-xl text-gray-900">AidWaive</div>
    </div>
  );
}

function DreamSchoolsBlock({ cards }) {
  if (!cards.length) return null;
  return (
    <section data-testid="find-dream-section">
      <SectionHead eyebrow="Tool 1" title="Your dream schools, graded" />
      <div className="grid gap-3">
        {cards.map(c => <DreamCard key={c.school.id} card={c} />)}
      </div>
    </section>
  );
}

function DreamCard({ card }) {
  const c = gradeColors(card.grade || "—");
  return (
    <div className="rounded-xl p-5" style={{ border: "1px solid #c7d2fe", background: "#fafbff" }}
      data-testid={`find-dream-card-${card.school.id}`}>
      <div className="grid sm:grid-cols-[auto_1fr] gap-5 items-start">
        <div className="font-heading flex items-center justify-center rounded-xl tabular-nums select-none"
          style={{ width: 76, height: 76, background: c.solid, color: "#ffffff", fontSize: "2.4rem" }}
          data-testid={`find-dream-grade-${card.school.id}`}>
          {card.grade}
        </div>
        <div className="min-w-0">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="font-medium text-gray-900 text-lg">{card.school.name}</div>
            <Confidence level={card.confidence} />
          </div>
          <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-3 flex-wrap">
            {card.school.state && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{card.school.state}</span>}
            {card.school.type && <span>{card.school.type}</span>}
            {card.graduation_rate && <span className="inline-flex items-center gap-1"><GraduationCap className="w-3 h-3" />{Math.round(card.graduation_rate * 100)}% grad</span>}
          </div>

          {card.net_price != null && (
            <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
              <Stat label="Verified net price" value={`$${card.net_price.toLocaleString()}/yr`} pos />
              {card.sticker_price ? <Stat label="Sticker price" value={`$${card.sticker_price.toLocaleString()}`} muted /> : null}
              {card.monthly_loan ? <Stat label="If fully borrowed" value={`$${card.monthly_loan.toLocaleString()}/mo`} /> : null}
            </div>
          )}

          {card.policy && (
            <div className="mt-3 rounded-md p-2.5 text-sm" style={{ background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#166534" }}>
              <strong className="font-semibold">Aid policy:</strong> {card.policy}{card.policy_no_loans ? " (no loans, grants only)" : ""}.
            </div>
          )}
          {card.explanation && (
            <p className="text-[15px] leading-relaxed text-gray-900 mt-3">{card.explanation}</p>
          )}
          {card.merit_note && (
            <p className="text-sm leading-relaxed text-gray-600 mt-2 italic">{card.merit_note}</p>
          )}
          {card.unusual_aid && (
            <div className="mt-3 flex items-start gap-1.5">
              <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "#1d4ed8" }} />
              <p className="text-sm text-gray-700"><strong>Most people don't know:</strong> {card.unusual_aid}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function HiddenGemsBlock({ data }) {
  const gems = data.gems || [];
  if (!gems.length) return (
    <section data-testid="find-gems-section">
      <SectionHead eyebrow="Tool 2" title="Hidden gems" />
      <p className="text-sm text-gray-700">{data.note || "No matches in our seed for this combo."}</p>
    </section>
  );
  return (
    <section data-testid="find-gems-section">
      <SectionHead
        eyebrow="Tool 2"
        title="Hidden gems most people miss"
        sub={data.flagship && data.flagship.net_price
          ? `Compared to ${data.flagship.name} (your state flagship at ~$${data.flagship.net_price.toLocaleString()}/yr for this bracket).`
          : "Cheaper than typical state flagships, with strong graduation rates."}
      />
      <div className="grid gap-3">
        {gems.map((g, i) => <GemCard key={g.school.id} gem={g} rank={i + 1} flagshipNet={data.flagship?.net_price} />)}
      </div>
    </section>
  );
}

function GemCard({ gem, rank, flagshipNet }) {
  const c = gradeColors(gem.grade || "—");
  const savings = flagshipNet ? Math.max(0, flagshipNet - gem.net_price) : null;
  return (
    <div className="rounded-xl p-5" style={{ border: "1px solid #c7d2fe", background: "#fafbff" }}
      data-testid={`find-gem-card-${gem.school.id}`}>
      <div className="grid sm:grid-cols-[auto_1fr_auto] gap-5 items-start">
        <div className="font-heading flex items-center justify-center rounded-xl tabular-nums select-none"
          style={{ width: 76, height: 76, background: c.solid, color: "#ffffff", fontSize: "2.4rem" }}>
          {gem.grade}
        </div>
        <div className="min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[11px] tabular-nums text-gray-400">#{rank}</span>
            <div className="font-medium text-gray-900 text-lg">{gem.school.name}</div>
            <Confidence level={gem.confidence} />
          </div>
          <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-3 flex-wrap">
            {gem.school.city && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{gem.school.city}, {gem.school.state}</span>}
            {gem.school.type && <span>{gem.school.type}</span>}
            {gem.graduation_rate && <span className="inline-flex items-center gap-1"><GraduationCap className="w-3 h-3" />{Math.round(gem.graduation_rate * 100)}% grad</span>}
          </div>

          <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
            <Stat label="Verified net price" value={`$${gem.net_price.toLocaleString()}/yr`} pos />
            {savings != null && <Stat label="vs state flagship" value={`-$${savings.toLocaleString()}/yr`} pos />}
            {gem.sticker_price && <Stat label="Sticker price" value={`$${gem.sticker_price.toLocaleString()}`} muted />}
          </div>

          {gem.policy && (
            <div className="mt-3 rounded-md p-2.5 text-sm" style={{ background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#166534" }}>
              <strong className="font-semibold">Aid policy:</strong> {gem.policy}{gem.policy_no_loans ? " (no loans, grants only)" : ""}.
            </div>
          )}
          <p className="text-[15px] leading-relaxed text-gray-900 mt-3">{gem.why}</p>
          {gem.hidden_insight && (
            <div className="mt-2 flex items-start gap-1.5">
              <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "#1d4ed8" }} />
              <p className="text-sm text-gray-800"><strong>Hidden insight:</strong> {gem.hidden_insight}</p>
            </div>
          )}
        </div>
        <Link to={`/find?school=${gem.school.id}`}
          className="hidden sm:inline-flex items-center gap-1 px-3 h-9 rounded-md text-xs font-medium hover:opacity-90 self-center"
          style={{ background: "#1d4ed8", color: "#ffffff" }}>
          Full score <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}

function SectionHead({ eyebrow, title, sub }) {
  return (
    <div className="mb-4">
      <div className="text-[11px] uppercase tracking-[0.22em] text-gray-400 mb-1">{eyebrow}</div>
      <h2 className="font-heading text-2xl sm:text-3xl text-gray-900 leading-tight">{title}</h2>
      {sub && <p className="text-sm text-gray-600 mt-1.5">{sub}</p>}
    </div>
  );
}

function Confidence({ level }) {
  const verified = level === "verified";
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-[0.1em] uppercase"
      style={{
        background: verified ? "#ecfdf5" : "#fef3c7",
        color: verified ? "#15803d" : "#92400e",
        border: `1px solid ${verified ? "#6ee7b7" : "#fcd34d"}`,
      }}>
      {verified ? <><CheckCircle2 className="w-2.5 h-2.5" /> Verified</> : <><AlertCircle className="w-2.5 h-2.5" /> Estimated</>}
    </span>
  );
}

function Stat({ label, value, pos, muted }) {
  return (
    <div className="rounded-lg px-2.5 py-2" style={{ background: "#f9fafb", border: "1px solid #e5e7eb" }}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-gray-400">{label}</div>
      <div className="mt-0.5 text-sm font-medium tabular-nums"
        style={{ color: pos ? "#15803d" : muted ? "#6b7280" : "#111928" }}>
        {value}
      </div>
    </div>
  );
}

function Footer() {
  return (
    <div className="flex items-center justify-between text-[11px] text-gray-400 pt-4 border-t border-gray-100">
      <div>Generated by AidWaive · aidwaive.com</div>
      <div>Free, no login</div>
    </div>
  );
}

/* ─── localStorage cache ──────────────────────────────────────────── */

function readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { v, ts } = JSON.parse(raw);
    if (!ts || Date.now() - ts > TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return v;
  } catch { return null; }
}
function writeCache(key, v) {
  try { localStorage.setItem(key, JSON.stringify({ v, ts: Date.now() })); } catch {}
}
